import { useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { PageContentWrapper } from '@unwrittenmedia/simple-components'
import { useAccount } from '@/contexts/AccountContext'
import { LoginCard } from '@/components/login/LoginCard'
import { useCompleteAuthRedirect, savePendingRedirectTarget, AuthMode } from '@/hooks/useFirebaseAuth'
// import { useProduct } from '@/hooks/query/useProduct'

/**
 * Login Page Component
 * 
 * see:
 * https://developer.apple.com/help/account/capabilities/configure-sign-in-with-apple-for-the-web/
 * 
 */

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>) => ({
    app: typeof search.app === 'string' && search.app.length > 0 ? search.app : undefined,
    to: typeof search.to === 'string' && search.to.length > 0 ? search.to : undefined,
    wantpopup: (search.wantpopup === true || search.wantpopup === 'true') ? true : undefined,
  }),
  component: LoginPage,
})

function LoginPage() {
  const navigate = useNavigate()
  const { dispatch, state: account } = useAccount()
  const { app: appId, to, wantpopup } = Route.useSearch()
  // const { data: product, isLoading: isProductLoading } = useProduct(appId)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [emailLinkSentTo, setEmailLinkSentTo] = useState<string | null>(null)

  const {
    isLoading: isFirebaseLoading,
    isCompletingRedirect,
    authenticate,
  } = useCompleteAuthRedirect({
    app: appId,
    to,
    isAuthenticated: account.isAuthenticated,
    dispatch,
    onError: setErrorMessage,
  })

  const handleGoogleLogin = async () => {
    setErrorMessage(null)
    try {
      if (wantpopup) {
        await authenticate(AuthMode.GooglePopup)
        return
      }
      // Hand off to the dedicated redirect-landing page so Google's round-trip lands there
      // instead of back on this page.
      await navigate({ to: '/login-gredir', search: { app: appId, to } })
    } catch {
      // error is already set in useFirebaseAuth onError
    }
  }

  const handleSendEmailLink = async (email: string) => {
    setErrorMessage(null)
    try {
      await authenticate(AuthMode.SendEmailLink, { email })
      setEmailLinkSentTo(email)
      return true
    } catch {
      // error is already set in useFirebaseAuth onError
      return false
    }
  }

  const handlePasswordLogin = async (email: string, password: string) => {
    setErrorMessage(null)
    try {
      await authenticate(AuthMode.PasswordLogin, { email, password })
    } catch {
      // error is already set in useFirebaseAuth onError
    }
  }

  const handlePasswordSignUp = async (email: string, password: string) => {
    setErrorMessage(null)
    try {
      await authenticate(AuthMode.PasswordSignUp, { email, password })
    } catch {
      // error is already set in useFirebaseAuth onError
    }
  }

  const isLoading = isFirebaseLoading || isCompletingRedirect

  const displayedError = errorMessage

  return (
    <PageContentWrapper className="bg-[radial-gradient(ellipse_at_top,_#bfdbfe_0%,_transparent_80%)]">
      <div className="flex flex-col justify-center min-h-screen">
        <LoginCard
          onGoogleLogin={handleGoogleLogin}
          onSendEmailLink={handleSendEmailLink}
          onPasswordLogin={handlePasswordLogin}
          onPasswordSignUp={handlePasswordSignUp}
          errorMessage={displayedError}
          isLoading={isLoading}
          appName={"Sermon Slides"}
          emailLinkSentTo={emailLinkSentTo}
          onClearEmailLinkSent={() => setEmailLinkSentTo(null)}
        />
      </div>
    </PageContentWrapper>
  )
}

